import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import NotificationBell from './NotificationBell';
import api, { adminAPI } from '../../api/client';
import {
  LayoutDashboard,
  ClipboardCheck,
  CreditCard,
  Sparkles,
  LogOut,
  ChevronRight,
  ChevronDown,
  Package,
  FileText,
  Users,
  BookOpen,
  MessageSquare,
  Settings,
  BarChart3,
  TrendingUp,
  Mail,
  Truck,
  HeadphonesIcon,
  Zap,
  Shield,
  History,
  Menu,
  Bell,
  Inbox,
  HelpCircle,
  PenTool,
  Target,
  Cookie,
  FileSignature,
  Briefcase,
  Handshake,
  Activity,
  Clock,
  AlertTriangle,
  Gauge,
  Megaphone,
  Link2,
  ListChecks,
  GitCompare,
  GitBranch,
  Rocket,
  Search,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { BRAND_LOGO_URL } from '../../config/branding';
import { isDiscoveryModuleEnabled } from '../../api/discoveryApi';
import SessionIdleGuard from '../SessionIdleGuard';
import AdminClientSupportSearch from './AdminClientSupportSearch';

/**
 * UnifiedAdminLayout - Enterprise-grade admin console with consolidated navigation
 */

const navSections = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { href: '/admin/dashboard', label: 'Overview', icon: LayoutDashboard },
      { href: '/admin/analytics', label: 'Analytics', icon: BarChart3, ownerOrAdminOnly: true },
      { href: '/admin/analytics/executive', label: 'Executive Overview', icon: TrendingUp, ownerOrAdminOnly: true },
      { href: '/admin/reporting', label: 'Reporting', icon: FileText },
      { href: '/admin/system-health', label: 'System Health', icon: Activity },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance Engine',
    icon: Shield,
    items: [
      { href: '/admin/compliance/registry', label: 'Policy Registry', icon: ListChecks },
      { href: '/admin/compliance/registry/preview', label: 'Registry Preview & Simulation', icon: GitCompare },
      { href: '/admin/compliance/registry/publish-queue', label: 'Registry Publish Queue', icon: Rocket },
      { href: '/admin/compliance/decisions', label: 'Decision Explorer', icon: GitBranch },
      { href: '/admin/ops/action-links', label: 'Action Links', icon: Link2 },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: ClipboardCheck,
    items: [
      { href: '/admin/ops', label: 'Overview', icon: LayoutDashboard },
      { href: '/admin/ops/identities', label: 'Identity lifecycle', icon: Shield },
      { href: '/admin/ops/maintenance', label: 'Jobs', icon: Settings },
      { href: '/admin/ops/contractors', label: 'Contractors', icon: Users },
      { href: '/admin/ops/risk', label: 'Risk & Insights', icon: BarChart3 },
      { href: '/admin/extraction-queue', label: 'Extraction Review', icon: FileText },
      { href: '/admin/communications', label: 'Communications', icon: Megaphone },
      { href: '/admin/ops/audit', label: 'Portfolio Compliance Audit', icon: History },
    ],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: Users,
    items: [
      { href: '/admin/dashboard', label: 'Clients', icon: Users, tabTarget: 'clients' },
      { href: '/admin/leads', label: 'Lead Management', icon: Target, badge: 'leads' },
      { href: '/admin/risk-leads', label: 'Risk Check Leads', icon: Activity },
      {
        href: '/admin/discovery/review',
        label: 'Discovery Review',
        icon: Search,
        ownerOrAdminOnly: true,
        discoveryModuleOnly: true,
      },
      { href: '/admin/talent-pool', label: 'Talent Pool', icon: Briefcase },
      { href: '/admin/partnership-enquiries', label: 'Partnership Enquiries', icon: Handshake },
      { href: '/admin/inbox/enquiries', label: 'Contact Enquiries', icon: Mail },
      { href: '/admin/orders', label: 'Orders Pipeline', icon: ClipboardCheck },
    ],
  },
  {
    id: 'products',
    label: 'Products & Billing',
    icon: Package,
    items: [
      { href: '/admin/services', label: 'Service Catalogue', icon: Package },
      { href: '/admin/intake-schema', label: 'Intake Schema', icon: PenTool },
      { href: '/admin/billing', label: 'Pricing & Billing', icon: CreditCard, tabTarget: 'overview' },
      { href: '/admin/billing', label: 'Pending Payments', icon: Clock, tabTarget: 'pending-payments' },
      { href: '/admin/pilot-invites', label: 'Founding Pilot Invites', icon: Sparkles, ownerOrAdminOnly: true },
      { href: '/admin/pilot-operations', label: 'Founding Pilot Operations', icon: Activity, ownerOrAdminOnly: true },
    ],
  },
  {
    id: 'content',
    label: 'Content & Experience',
    icon: FileText,
    items: [
      { href: '/admin/site-builder', label: 'Site Builder', icon: PenTool },
      { href: '/admin/knowledge-base', label: 'Knowledge Centre', icon: BookOpen },
      { href: '/admin/blog', label: 'Blog / Insights', icon: FileText },
      { href: '/admin/content/faqs', label: 'FAQ Management', icon: HelpCircle },
      { href: '/admin/content/feedback', label: 'Insights Feedback', icon: MessageSquare },
      { href: '/admin/support/responses', label: 'Canned Responses', icon: MessageSquare },
      { href: '/admin/settings/legal', label: 'Legal Pages', icon: FileSignature },
      { href: '/admin/marketing/newsletter', label: 'Newsletter', icon: Mail },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    icon: HeadphonesIcon,
    items: [
      { href: '/admin/support', label: 'Support Dashboard', icon: HeadphonesIcon },
      { href: '/admin/postal-tracking', label: 'Postal Tracking', icon: Truck, badge: 'postal' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings,
    items: [
      { href: '/admin/team', label: 'Team Permissions', icon: Shield },
      { href: '/admin/prompts', label: 'Prompt Manager', icon: Sparkles },
      { href: '/admin/enablement', label: 'Enablement Engine', icon: Zap },
      { href: '/admin/privacy/consent', label: 'Privacy & Consent', icon: Cookie },
      { href: '/admin/ops/feature-controls', label: 'Feature Controls', icon: Zap },
      { href: '/admin/control-centre', label: 'Platform status', icon: Gauge, ownerOrAdminOnly: true },
      { href: '/admin/dashboard', label: 'Legacy Mongo rules (read-only)', icon: Settings, tabTarget: 'rules' },
      { href: '/admin/dashboard', label: 'Email Templates', icon: Mail, tabTarget: 'templates' },
      { href: '/admin/dashboard', label: 'Email Delivery', icon: Mail, tabTarget: 'emailDelivery' },
      { href: '/admin/automation', label: 'Automation Control Centre', icon: Zap },
      { href: '/admin/ops/evidence-timeline', label: 'Operational Evidence Timeline', icon: GitBranch },
      { href: '/admin/incidents', label: 'Incidents', icon: AlertTriangle, badge: 'incidents' },
      { href: '/admin/security', label: 'Security Monitoring', icon: Shield },
      { href: '/admin/dashboard', label: 'System Audit Logs', icon: History, tabTarget: 'audit' },
      { href: '/admin/notifications/inbox', label: 'Notification Inbox', icon: Inbox },
      { href: '/admin/notifications/preferences', label: 'Notification Preferences', icon: Bell },
    ],
  },
];

// Section IDs that each staff role can see (OWNER/ADMIN see all)
const SECTIONS_FOR_SUPPORT = ['dashboard', 'support'];
const SECTIONS_FOR_CONTENT = ['dashboard', 'content'];
// Auditor: read-only; dashboard, compliance + operations (view-only), and system audit (system section)
const SECTIONS_FOR_AUDITOR = ['dashboard', 'compliance', 'operations', 'system'];

// Sidebar content component - defined outside to avoid re-creation
const SidebarContent = ({ 
  sidebarOpen, 
  expandedSections, 
  toggleSection, 
  handleNavClick, 
  isActive, 
  badges, 
  navigate, 
  user, 
  handleLogout,
  visibleSections,
}) => (
  <div className="flex flex-col h-full">
    {/* Logo */}
    <div className="p-4 border-b border-gray-200">
      <Link to="/admin/dashboard" className="flex items-center space-x-2">
        <img 
          src={BRAND_LOGO_URL} 
          alt="Pleerity" 
          className={cn(
            "transition-all duration-300",
            sidebarOpen ? "h-10 w-auto" : "h-9 w-9 object-contain"
          )}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
          }}
        />
        {sidebarOpen && (
          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Admin Console</span>
        )}
      </Link>
    </div>

    {/* Navigation */}
    <nav className="flex-1 overflow-y-auto py-4 px-3">
      {(visibleSections || navSections).map((section) => (
        <div key={section.id} className="mb-2">
          {/* Section Header */}
          <button
            onClick={() => toggleSection(section.id)}
            className={cn(
              "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors",
              expandedSections.includes(section.id)
                ? "bg-gray-100 text-midnight-blue"
                : "text-gray-600 hover:bg-gray-50"
            )}
          >
            <div className="flex items-center gap-2">
              <section.icon className="w-4 h-4" />
              {sidebarOpen && <span>{section.label}</span>}
            </div>
            {sidebarOpen && (
              expandedSections.includes(section.id) 
                ? <ChevronDown className="w-4 h-4" />
                : <ChevronRight className="w-4 h-4" />
            )}
          </button>

          {/* Section Items */}
          {expandedSections.includes(section.id) && sidebarOpen && (
            <div className="mt-1 ml-4 space-y-1">
              {section.items.map((item) => (
                <button
                  key={item.href + (item.tabTarget || '')}
                  onClick={() => handleNavClick(item)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                    isActive(item.href, item.tabTarget)
                      ? "bg-electric-teal text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && badges[item.badge] > 0 && (
                    <span className={cn(
                      "px-2 py-0.5 text-xs font-bold rounded-full",
                      isActive(item.href, item.tabTarget)
                        ? "bg-white/20 text-white"
                        : "bg-red-500 text-white"
                    )}>
                      {badges[item.badge]}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>

    {/* AI Assistant Quick Access */}
    <div className="p-4 border-t border-gray-200">
      <button
        onClick={() => navigate('/admin/assistant')}
        className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-600 hover:to-indigo-700 transition-all shadow-lg"
      >
        <Sparkles className="w-5 h-5" />
        {sidebarOpen && (
          <div className="text-left">
            <span className="font-medium block">AI Assistant</span>
            <span className="text-xs opacity-80">Ask anything</span>
          </div>
        )}
      </button>
    </div>

    {/* User Info */}
    {sidebarOpen && (
      <div className="p-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.full_name || 'Admin'}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>
    )}
  </div>
);

const UnifiedAdminLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user, isSupport, isContent, isAuditor, isAdmin, isOwner } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(['dashboard', 'compliance', 'crm']);
  const [badges, setBadges] = useState({ leads: 0, postal: 0, incidents: 0 });
  const [openP0P1Count, setOpenP0P1Count] = useState(0);
  const visibleSections = (() => {
    let sections = navSections;
    if (isSupport?.()) sections = navSections.filter((s) => SECTIONS_FOR_SUPPORT.includes(s.id));
    else if (isContent?.()) sections = navSections.filter((s) => SECTIONS_FOR_CONTENT.includes(s.id));
    else if (isAuditor?.()) sections = navSections.filter((s) => SECTIONS_FOR_AUDITOR.includes(s.id));
    const isOwnerOrAdmin = isOwner?.() || isAdmin?.();
    const isAuditorRole = isAuditor?.();
    const discoveryEnabled = isDiscoveryModuleEnabled();
    return sections.map((sec) => ({
      ...sec,
      items: (sec.items || []).filter((item) => {
        if (item.discoveryModuleOnly && !discoveryEnabled) return false;
        if (isAuditorRole && sec.id === 'system') return item.tabTarget === 'audit';
        // Auditor can see compliance engine + operations items (read-only; write actions 403 on backend)
        if (isAuditorRole && (sec.id === 'operations' || sec.id === 'compliance')) return true;
        return isOwnerOrAdmin || !item.ownerOrAdminOnly;
      }),
    }));
  })();

  // Fetch badge counts and observability (P0/P1 incidents)
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const [leadsRes, postalRes, healthRes] = await Promise.all([
          api.get('/admin/leads/notifications').catch(() => ({ data: { total_alerts: 0 } })),
          api.get('/admin/orders/postal/pending').catch(() => ({ data: { total: 0 } })),
          adminAPI.getObservabilityHealthSummary().catch(() => ({ data: { open_p0_p1_count: 0 } })),
        ]);
        const p0p1 = healthRes.data?.open_p0_p1_count ?? 0;
        setBadges({
          leads: leadsRes.data?.total_alerts || 0,
          postal: postalRes.data?.total || 0,
          incidents: p0p1,
        });
        setOpenP0P1Count(p0p1);
      } catch (err) {
        console.error('Failed to fetch badges:', err);
      }
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const isActive = (path, tabTarget) => {
    if (tabTarget === 'overview') {
      return location.pathname === path && !location.search.includes('tab=pending-payments');
    }
    if (tabTarget) {
      return location.pathname === path && location.search.includes(`tab=${tabTarget}`);
    }
    return location.pathname === path;
  };

  const handleNavClick = (item) => {
    if (item.tabTarget) {
      navigate(`${item.href}?tab=${item.tabTarget}`);
    } else {
      navigate(item.href);
    }
    setMobileMenuOpen(false);
  };

  const sidebarProps = {
    sidebarOpen,
    expandedSections,
    toggleSection,
    handleNavClick,
    isActive,
    badges,
    navigate,
    user,
    handleLogout,
    visibleSections,
  };

  return (
    <SessionIdleGuard>
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300",
          sidebarOpen ? "w-64" : "w-20"
        )}
      >
        <SidebarContent {...sidebarProps} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-white shadow-xl">
            <SidebarContent {...sidebarProps} />
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Left: Menu Toggle & Search */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  if (window.innerWidth >= 1024) {
                    setSidebarOpen(!sidebarOpen);
                  } else {
                    setMobileMenuOpen(!mobileMenuOpen);
                  }
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              <div className="hidden md:block relative w-72 shrink-0">
                <AdminClientSupportSearch variant="header" limit={8} />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              <NotificationBell />
              
              <Link
                to="/support/knowledge-base"
                target="_blank"
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                title="Help Center"
              >
                <HelpCircle className="w-5 h-5" />
              </Link>
              
              <div className="hidden lg:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-gray-500 hover:text-red-600"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 flex flex-col min-w-0 overflow-auto">
          {openP0P1Count > 0 && (
            <div className="flex-shrink-0 bg-red-600 text-white px-4 py-2 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span className="font-medium">
                  {openP0P1Count} open P0/P1 incident{openP0P1Count !== 1 ? 's' : ''}
                </span>
              </div>
              <Link
                to="/admin/incidents?status=open"
                className="text-sm font-medium underline hover:no-underline"
              >
                View incidents →
              </Link>
            </div>
          )}
          <div className="flex-1 p-4 lg:p-6 overflow-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
    </SessionIdleGuard>
  );
};

export default UnifiedAdminLayout;
